import React from "react";
import { Box, Chip, Button, Typography } from "@mui/material/";
import { format } from "../../lib";
import { strings } from "../../i18n";
import tableStyles from "./styles";

/**
 * Renders a readable value for an active column filter based on its
 * filterVariant. Handles multi-select (array), date-range and numeric range
 * (both [a, b] tuples), and plain scalar values.
 */
const formatFilterValue = (column, value) => {
    const variant = column?.columnDef?.filterVariant;

    if (Array.isArray(value)) {
        if (variant === "date-range") {
            const [start, end] = value;
            const s = start ? format.date(start) : "";
            const e = end ? format.date(end) : "";
            if (s && e) return `${s} – ${e}`;
            if (s) return `${strings("from")} ${s}`;
            if (e) return `${strings("until")} ${e}`;
            return "";
        }
        if (variant === "range") {
            const [min, max] = value;
            if (min != null && min !== "" && max != null && max !== "")
                return `${min} – ${max}`;
            if (min != null && min !== "") return `≥ ${min}`;
            if (max != null && max !== "") return `≤ ${max}`;
            return "";
        }
        return value.filter((v) => v != null && v !== "").join(", ");
    }

    return value == null ? "" : String(value);
};

/**
 * Active-filter chips bar. Renders below the table toolbar so it's obvious
 * which filters (and global search) are applied the moment the table is seen.
 * Reads live MRT state; renders nothing when no filter/search is active.
 */
const ActiveFilters = ({ table }) => {
    const { columnFilters = [], globalFilter } = table.getState();
    const hasGlobal =
        globalFilter != null && String(globalFilter).trim() !== "";

    const columnIds = new Set(table.getAllLeafColumns().map((c) => c.id));
    const activeFilters = columnFilters.filter((f) => columnIds.has(f.id));

    if (activeFilters.length === 0 && !hasGlobal) return null;

    const clearAll = () => {
        table.resetColumnFilters();
        table.setGlobalFilter("");
    };

    return (
        <Box sx={tableStyles.activeFiltersBar}>
            <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600 }}
            >
                {`${strings("filters")}:`}
            </Typography>

            {hasGlobal && (
                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${strings("search")}: "${globalFilter}"`}
                    onDelete={() => table.setGlobalFilter("")}
                />
            )}

            {activeFilters.map((f) => {
                const column = table.getColumn(f.id);
                const header = column?.columnDef?.header;
                const label = typeof header === "string" ? header : f.id;
                const val = formatFilterValue(column, f.value);
                return (
                    <Chip
                        key={f.id}
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={val ? `${label}: ${val}` : label}
                        onDelete={() => column?.setFilterValue(undefined)}
                    />
                );
            })}

            <Button
                size="small"
                onClick={clearAll}
                sx={{ ml: "auto", textTransform: "none" }}
            >
                {strings("clearAll")}
            </Button>
        </Box>
    );
};

export default ActiveFilters;
